import { useCallback, useEffect, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
    profilbildSuchen,
    profilbildUebernehmen,
    type Bildvorschlag,
    type ProfilbildAntwort,
} from "../../services/firmendatenService";

/**
 * Ein Profilbild aus LinkedIn oder Xing für einen Menschen in der Akte.
 *
 * **Warum eine Auswahl und kein Automatismus.** Eine Suche nach „Christian
 * Klein SAP" liefert den SAP-Vorstand, einen Klinikstrategen und einen
 * Finanzmanager gleichen Namens — jeder mit Foto, alle drei plausibel. Ein
 * automatisch gesetztes Bild wäre in diesen Fällen das Gesicht einer fremden
 * Person in der Kundenakte, und niemand würde es je bemerken. Deshalb steht
 * hier, was gefunden wurde, mit dem, was für den Treffer spricht — und ein
 * Mensch entscheidet.
 *
 * Die Reihenfolge ist eine Sortierhilfe, keine Empfehlung: Nur wo Name UND
 * Firma im Treffer stehen, ist er wirklich belastbar; sonst warnt der Dialog.
 */
const useStyles = makeStyles({
    kopfzeile: { fontSize: "13px", color: "#6b5a66", marginBottom: "16px", lineHeight: 1.5 },
    mitte: { textAlign: "center", ...shorthands.padding("34px", "0"), color: "#6b5a66", fontSize: "13.5px" },
    raster: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(158px, 1fr))",
        ...shorthands.gap("13px"),
    },
    kachel: {
        ...shorthands.borderRadius("11px"),
        ...shorthands.border("2px", "solid", "transparent"),
        ...shorthands.padding("9px"),
        backgroundColor: "#f8f4f8",
        cursor: "pointer",
        textAlign: "left",
        display: "block",
        width: "100%",
        transitionProperty: "border-color, background-color",
        transitionDuration: "120ms",
        ":hover": { backgroundColor: "#f1e9f0" },
    },
    gewaehlt: {
        ...shorthands.borderColor("#7d2169"),
        backgroundColor: "#f1e9f0",
    },
    bild: {
        width: "100%",
        aspectRatio: "1",
        objectFit: "cover",
        ...shorthands.borderRadius("8px"),
        display: "block",
        backgroundColor: "#fff",
    },
    netzwerk: { fontSize: "11px", fontWeight: 700, color: "#7d2169", marginTop: "8px", letterSpacing: ".03em" },
    beschreibung: { fontSize: "12px", color: "#3d2d39", lineHeight: 1.35, marginTop: "3px" },
    beleg: { fontSize: "11px", color: "#6b5a66", marginTop: "5px", lineHeight: 1.35 },
    warnung: {
        ...shorthands.padding("10px", "12px"),
        ...shorthands.borderRadius("9px"),
        backgroundColor: "#fdf3e7",
        color: "#7a4b12",
        fontSize: "12.5px",
        lineHeight: 1.5,
        marginBottom: "14px",
    },
    fehler: { color: "#a3162c", fontSize: "13px", marginBottom: "12px" },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
    knopf: {
        ...shorthands.padding("9px", "16px"),
        ...shorthands.borderRadius("8px"),
        ...shorthands.border("1px", "solid", "#d9c6d4"),
        backgroundColor: "#fff",
        color: "#3d2d39",
        fontSize: "13.5px",
        fontWeight: 600,
        cursor: "pointer",
    },
    haupt: {
        backgroundColor: "#7d2169",
        color: "#fff",
        ...shorthands.borderColor("#7d2169"),
        ":hover": { backgroundColor: "#591149", ...shorthands.borderColor("#591149") },
        ":disabled": { backgroundColor: "#d9c6d4", ...shorthands.borderColor("#d9c6d4"), cursor: "default" },
    },
});

/** Ab hier nennt der Treffer Name UND Firma — erst dann ist er belastbar. */
const BELASTBAR = 0.8;

export default function ProfilbildDialog({
    contactId,
    onClose,
    onUebernommen,
}: {
    contactId: number;
    onClose: () => void;
    onUebernommen: () => void;
}) {
    const s = useStyles();
    const [laeuft, setLaeuft] = useState(true);
    /** Zweite Stufe: Der erste Aufruf ist zurück, die Suche läuft noch. */
    const [dauert, setDauert] = useState(false);
    const [speichert, setSpeichert] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [antwort, setAntwort] = useState<ProfilbildAntwort | null>(null);
    const [gewaehlt, setGewaehlt] = useState<Bildvorschlag | null>(null);

    const holen = useCallback(async () => {
        setLaeuft(true);
        setFehler(null);
        try {
            setAntwort(await profilbildSuchen(contactId, setDauert));
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
            setDauert(false);
        }
    }, [contactId]);

    useEffect(() => {
        void holen();
    }, [holen]);

    const uebernehmen = async () => {
        if (!gewaehlt) return;
        setSpeichert(true);
        setFehler(null);
        try {
            await profilbildUebernehmen(contactId, gewaehlt.bild);
            onUebernommen();
            onClose();
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setSpeichert(false);
        }
    };

    const treffer = antwort?.vorschlaege ?? [];
    const unsicher = treffer.length > 0 && !treffer.some((v) => v.guete >= BELASTBAR);

    return createPortal(
        <div className="kvp-rd-scrim" onClick={speichert ? undefined : onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">Profilbild · LinkedIn und Xing</div>
                            <h2>{antwort?.person.name || "Profilbild suchen"}</h2>
                        </div>
                        {!speichert && (
                            <button className="dm-x" onClick={onClose} aria-label="Schließen">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                <div className="dm-body">
                    {laeuft && (
                        <div className={s.mitte}>
                            {dauert
                                ? "Die Suche läuft noch — LinkedIn und Xing brauchen zusammen etwa eine halbe Minute …"
                                : "In den Netzwerken wird gesucht …"}
                        </div>
                    )}
                    {fehler && <div className={s.fehler}>{fehler}</div>}

                    {!laeuft && antwort && (
                        <>
                            {antwort.person.firma && (
                                <div className={s.kopfzeile}>
                                    Gesucht wurde nach <strong>{antwort.person.name}</strong> bei{" "}
                                    <strong>{antwort.person.firma}</strong>. Bitte prüfen, ob das
                                    wirklich dieselbe Person ist — Namensvettern sehen hier genauso
                                    plausibel aus.
                                </div>
                            )}

                            {unsicher && (
                                <div className={s.warnung}>
                                    Kein Treffer nennt Name und Firma zugleich. Das heißt nicht, dass
                                    keiner stimmt — es heißt, dass nichts dafür spricht außer dem
                                    Namen.
                                </div>
                            )}

                            {antwort.hinweise.map((h, i) => (
                                <div key={i} className={s.kopfzeile}>{h}</div>
                            ))}

                            {treffer.length > 0 && (
                                <div className={s.raster}>
                                    {treffer.map((v, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className={mergeClasses(s.kachel, gewaehlt === v && s.gewaehlt)}
                                            onClick={() => setGewaehlt(gewaehlt === v ? null : v)}
                                            aria-pressed={gewaehlt === v}
                                        >
                                            <img className={s.bild} src={v.bild} alt="" />
                                            <div className={s.netzwerk}>{v.netzwerkName.toUpperCase()}</div>
                                            <div className={s.beschreibung}>{v.beschreibung}</div>
                                            {v.begruendung.length > 0 && (
                                                <div className={s.beleg}>{v.begruendung.join(" · ")}</div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="dm-foot">
                    <div className={s.fuss}>
                        <button className={s.knopf} onClick={onClose} disabled={speichert}>
                            Abbrechen
                        </button>
                        <button
                            className={mergeClasses(s.knopf, s.haupt)}
                            onClick={() => void uebernehmen()}
                            disabled={!gewaehlt || speichert}
                        >
                            {speichert ? "Wird übernommen …" : "Als Profilbild übernehmen"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
